import { useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Typography, Tooltip, Box, Stack, IconButton, Skeleton } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useSelector } from '../../../../../redux/store';
import Iconify from '../../../../../reusable-components/iconify';
import { useTabs } from '../../../../../context/TabContext';
import CustomDataGrid from '../../../../../reusable-components/datagrid/CustomDataGrid';
import { InvoicingPath, RelatedInvoiceType } from '../../../../../utils/globalEnums';

const BLUE = '#208EE0';

export const RelatedInvoices = ({ tabs, logHeight = 400, highlightedRows }) => {
  const [expand, setExpand] = useState(true);
  const navigate = useNavigate();
  const { id } = useParams();
  const { addTab } = useTabs();

  const {
    data: {
      relatedInvoices: { invoiceId: relatedForId, related = [] },
    },
  } = useSelector((state) => state.invoicing);

  // Generate a unique key based on the data to force re-render when data changes
  const gridKey = useMemo(() => {
    return `related-invoices-${related.length}-${JSON.stringify(related.map((i) => i.id))}`;
  }, [related]);
  const columns = [
    {
      field: 'vendorInvoiceId',
      headerName: 'Invoice #',
      flex: 3.5,
      sortable: true,
      filterable: true,
      renderCell: (params) => {
        const isPotentialDuplicate =
          params.row.type === RelatedInvoiceType.PotentialDuplicate ||
          params.row.type === RelatedInvoiceType.PotentialSpaceDuplicate;
        return (
          <Tooltip
            title={
              isPotentialDuplicate
                ? `Invoice #${params.value} may be a duplicate of the current invoice.`
                : params.value || ''
            }
            arrow
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {isPotentialDuplicate && (
                <Typography display={'flex'} color="error.main">
                  <ErrorOutlineIcon fontSize={'small'} />
                </Typography>
              )}
              <span
                style={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {params.value}
              </span>
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: 'vendor',
      headerName: 'Vendor',
      flex: 3.5,
      sortable: true,
      filterable: true,
      renderCell: (params) => (
        <Tooltip title={params.value || ''} arrow>
          <Box
            sx={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              textAlign: 'left',
              width: '100%',
            }}
          >
            {params.value}
          </Box>
        </Tooltip>
      ),
    },
    {
      field: 'total',
      headerName: 'Total',
      flex: 2.5,
      type: 'number',
      sortable: true,
      filterable: true,
      renderCell: (params) => {
        const val = Number(params.value) || 0;
        return (
          <Tooltip title={`$${val.toFixed(2)}`} arrow>
            <span style={{ display: 'block', textAlign: 'right', whiteSpace: 'nowrap' }}>${val.toFixed(2)}</span>
          </Tooltip>
        );
      },
    },
  ];
  const getRowClassName = (params) => {
    const isHighlighted = highlightedRows?.includes(params.row.id);
    const baseClass = InvoicingPath[params.row.path] === 'previous' ? 'sent-invoice-row' : 'unsent-invoice-row';
    return isHighlighted ? `${baseClass} highlighted-row` : baseClass;
  };

  return (
    <Box
      sx={{
        mb: 2,
        display: 'flex',
        flexDirection: 'column',
        padding: '15px',
        pt: 0,
        borderRadius: '4px',
        boxShadow: '0 3px 6px 0 rgba(0,0,0,0.16)',
        backgroundColor: '#fff',
        border: 'solid 1px #c1c9d0', // static border for container
      }}
    >
      <Stack direction="row" sx={{ alignItems: 'center', px: 1, pt: 1 }}>
        {tabs}
        <Box sx={{ ml: 'auto' }}>
          <Tooltip title={expand ? 'less' : 'more'}>
            <IconButton
              onClick={() => setExpand(!expand)}
              sx={{
                color: BLUE,
                padding: 0,
                height: 'fit-content',
                mt: 0.5,
              }}
            >
              <Iconify icon={`ic:twotone-expand-${expand ? 'less' : 'more'}`} width={26} height={26} />
            </IconButton>
          </Tooltip>
        </Box>
      </Stack>

      {expand && (
        <Box sx={{ height: logHeight, overflow: 'hidden' }}>
          {relatedForId !== Number(id) ? (
            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Skeleton variant="rectangular" height={36} sx={{ borderRadius: 1 }} />
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} variant="rectangular" height={44} sx={{ borderRadius: 1 }} />
              ))}
            </Box>
          ) : related.length > 0 ? (
            <CustomDataGrid
              key={gridKey}
              gridId={gridKey}
              data={related}
              gridColumns={columns}
              hideFooter
              hideFilterSettings
              autoHeight={false}
              onRowClick={(params) => {
                const selection = window.getSelection()?.toString();
                if (selection && selection.length > 0) return;
                addTab();
                navigate(`/dashboard/invoicing/${InvoicingPath[params.row.path]}/${params.row.id}`);
              }}
              getRowClassName={getRowClassName}
              sx={{
                border: 'none',
                '& .MuiDataGrid-main': {
                  maxHeight: logHeight,
                  overflowY: 'auto',
                },
                '& .MuiDataGrid-virtualScroller': {
                  scrollbarWidth: 'thin',
                },
                '& .MuiDataGrid-virtualScroller::-webkit-scrollbar': {
                  width: '8px',
                },
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: '#fff',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  borderBottom: `1px solid ${BLUE}`,
                  position: 'sticky',
                  top: 0,
                  zIndex: 2,
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  color: BLUE,
                },
                '& .MuiDataGrid-cell': {
                  borderBottom: `1px solid ${BLUE}`,
                  fontSize: '0.75rem',
                },
                '& .MuiDataGrid-row.sent-invoice-row': {
                  borderLeft: '4px solid red',
                  backgroundColor: '#fff',
                },
                '& .MuiDataGrid-row.unsent-invoice-row': {
                  borderLeft: '4px solid orange',
                  backgroundColor: '#fff',
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: '#f5faff',
                },
                '& .MuiDataGrid-columnSeparator': {
                  display: 'none',
                },
                '& .MuiDataGrid-row.highlighted-row': {
                  animation: 'pulseHighlight 2s ease-in-out',
                },
                '@keyframes pulseHighlight': {
                  '0%': { backgroundColor: '#e8f4ff' },
                  '50%': { backgroundColor: '#b3d9ff' },
                  '100%': { backgroundColor: 'transparent' },
                },
              }}
            />
          ) : (
            <Typography variant="body2" sx={{ textAlign: 'center', mt: 2, color: 'text.secondary' }}>
              No related invoices.
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};
